// V2 Salvage section config — the page the "/player/salvage/v2" tab embeds.
//
// There is NO new app and NO new token handshake here. The Salvager is a page inside the HotUtilsV2-Player
// app (HotUtilsV2 `docs/design/salvage-and-crafting.md`), exactly as V2-Datacrons, V2-Player Events and
// V2-Mods are. So this module only names the URL; the origin, the enablement gate and the parent-token
// minting all come from utils/v2player.ts unchanged — the same division of labour utils/v2mods.ts uses.
//
// ⚠️ This tab is ADDITIVE. The legacy Salvage page (`/player/salvage`, pages/Salvage) stays exactly where it
// is; nothing here replaces or hides it.

import { getV2PlayerUrl, isV2PlayerEnabled } from "./v2player";

/**
 * The Player app's Salvager page.
 *
 * 🔴 The path is the whole contract with the embedded app: `App.tsx` there selects the page with
 * `pathname.startsWith('/salvage')` and FALLS THROUGH TO DATACRONS otherwise. A wrong or dropped path does
 * not fail loudly — the app boots and shows Datacrons, so the tab looks like it simply does not work yet.
 *
 * ⚠️ Note the asymmetry with the parent route. The website frames this at `/player/salvage/v2` (beside the
 * legacy page it will eventually replace), but the app is framed at `/salvage` — the embedded app has its own
 * flat path space and knows nothing about where the parent filed the tab.
 *
 * One URL, no sub-paths: the table, the craft dialog and the scrap confirmation are all one page.
 */
export function getV2SalvageUrl(): string {
  return getV2PlayerUrl() + "/salvage";
}

/**
 * The V2-Salvage tab has no gate of its own: it IS the Player app, so it appears exactly where V2-Datacrons,
 * V2-Player Events and V2-Mods do — development.hotutils.com and localhost today. Named separately rather
 * than calling isV2PlayerEnabled() at the call sites so the section's gate is one thing to change on the day
 * Salvage graduates ahead of (or behind) the rest of that app.
 *
 * 🔴 The gate matters more here than on the read-only tabs: the page CRAFTS and SCRAPS on the player's live
 * game account, and scrapping destroys gear irreversibly. Until the first live exchange has been watched on a
 * test account, this must not surface anywhere but development.
 *
 * The ROUTE and the MENU ITEM must both use this, or the tab appears where it cannot work.
 */
export function isV2SalvageEnabled(): boolean {
  return isV2PlayerEnabled();
}
